import { useState, useCallback } from 'react';

interface LimitError {
  error: string;
  limit_type: string;
  current: number;
  limit: number;
  remaining: number;
  attempted?: number;
}

interface LimitHandlerState {
  showUpgradeModal: boolean;
  limitType: 'items' | 'ai_scans' | 'storage' | null;
  limitMessage: string | null;
}

export const useLimitHandler = () => {
  const [state, setState] = useState<LimitHandlerState>({
    showUpgradeModal: false,
    limitType: null,
    limitMessage: null,
  });

  const handleLimitError = useCallback((error: LimitError) => {
    // Map API limit types to modal types
    const limitTypeMap: Record<string, 'items' | 'ai_scans' | 'storage'> = {
      'items': 'items',
      'ai_scans_per_month': 'ai_scans',
      'storage_mb': 'storage',
    };

    const limitType = limitTypeMap[error.limit_type] || 'items';
    
    // Create custom message based on error details. Use wording that
    // works for both single and multiple potential items.
    let message = error.error;
    if (typeof error.remaining === 'number') {
      message = `You tried to add new items, but you have ${error.remaining} slot${error.remaining === 1 ? '' : 's'} remaining.`;
    }

    setState({
      showUpgradeModal: true,
      limitType,
      limitMessage: message,
    });
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setState({
      showUpgradeModal: false,
      limitType: null,
      limitMessage: null,
    });
  }, []);

  const checkResponseForLimit = useCallback(async (response: Response) => {
    // Only bother inspecting non-OK responses; callers typically call this
    // after seeing !response.ok.
    if (response.ok) return false;

    try {
      // Work on a clone so callers can still consume the original
      const clone = response.clone();
      let errorData: any = null;
      try {
        errorData = await clone.json();
      } catch {
        // If body is not JSON, fall through to text handling
      }

      // Preferred path: backend sends structured limit error with limit_type
      if (errorData && errorData.limit_type) {
        handleLimitError(errorData as LimitError);
        return true; // Limit error handled
      }

      // Fallback: treat any error whose message mentions "item limit" as an
      // item-limit error so we can still show the shared UpgradePromptModal.
      const rawMessage =
        (errorData && typeof errorData.error === 'string' && errorData.error) ||
        (await clone.text().catch(() => '')) ||
        '';

      if (rawMessage.toLowerCase().includes('item limit')) {
        handleLimitError({
          error: rawMessage || 'Item limit reached',
          limit_type: 'items',
          current: typeof errorData?.current === 'number' ? errorData.current : 0,
          limit: typeof errorData?.limit === 'number' ? errorData.limit : 0,
          remaining: typeof errorData?.remaining === 'number' ? errorData.remaining : 0,
        } as LimitError);
        return true;
      }
    } catch {
      // Not a limit error, or response body unreadable; let caller handle.
    }

    return false; // Not a limit error
  }, [handleLimitError]);

  return {
    showUpgradeModal: state.showUpgradeModal,
    limitType: state.limitType,
    limitMessage: state.limitMessage,
    handleLimitError,
    closeUpgradeModal,
    checkResponseForLimit,
  };
};
